import { useLoad } from "@redotech/react-util/load";
import { ReturnableItem } from "@redotech/redo-model/draft-return/draft-return-items";
import { getReturnableItemTotalReturnValue } from "@redotech/redo-model/draft-return/util";
import { ExchangeGroup } from "@redotech/redo-model/team";
import {
  getProductsByCollection,
  getProductsByTags,
  ProductInfo as ShopifyStorefrontProduct,
} from "@redotech/shopify-storefront/products";
import { ShopifyStorefrontClient } from "@redotech/shopify-storefront/storefront-client";
import {
  convertShopifyIdToGid,
  ShopifyIdType,
} from "@redotech/util/shopify-id";
import { useContext, useMemo, useState } from "react";
import { ReturnContext } from "../../contexts/return";
import { SuggestedExchangeOptionsContext } from "../../contexts/suggested-exchange-options";
import { ReturnAppSettings } from "../../settings";
import { storefrontProductToProductData } from "../../util";
import {
  getSuggestedExchangeResult,
  SuggestedExchangeEventType,
  SuggestedExchangeResult,
} from "../../utils/suggested-exchange-events";
import { RecommendedExchangeProductData } from "../use-recommended-exchange/use-recommended-exchange";
import { ProductData, ProductVariantData } from "./use-flow";

interface UseExchangeGroupsResponse {
  products: ProductData[];
  selectedProduct: ProductVariantData | undefined;
  onSelectProduct: (
    selectedProductAndVariant:
      | ProductVariantData
      | { productIdToRemove: string },
  ) => void;
  onSubmitProduct: (selectedProductAndVariant: ProductVariantData) => void;
  title: string;
  subtitle: string;
  loading: boolean;
  totalReturnableValue: number;
  getExchangeGroup: (productId: string) => ExchangeGroup | undefined;
}

export interface SuggestedExchangeGroupOptions {
  shouldFireEvent: boolean;
  fromSuggestedPreview: boolean;
  groupsByProductId: ExchangeGroupByProductId | undefined;
  originalProduct: ProductData | undefined;
  suggestedVariantOptions?: { name: string; values: string[] }[];
  disableExchangeValueAsPriceDifference: boolean;
}

export type ExchangeGroupByProductId = Map<
  string,
  {
    group: ExchangeGroup;
    product: ShopifyStorefrontProduct;
    allGroups: ExchangeGroup[];
  }
>;

export async function getExchangeGroupsByProductId(
  groups: ExchangeGroup[],
  settings: ReturnAppSettings | undefined,
): Promise<ExchangeGroupByProductId> {
  const groupsByProductId = new Map<
    string,
    {
      group: ExchangeGroup;
      product: ShopifyStorefrontProduct;
      allGroups: ExchangeGroup[];
    }
  >();

  if (!settings) {
    return groupsByProductId;
  }
  const storefrontClient = new ShopifyStorefrontClient(
    settings.storeUrl,
    settings.storefrontAccessToken,
  );

  for (const group of groups) {
    let groupProducts: ShopifyStorefrontProduct[] = [];

    switch (group.type) {
      case "name:collection":
      case "tag:collection":
      case "collection:collection":
        groupProducts = await getProductsByCollection(
          storefrontClient,
          convertShopifyIdToGid(
            group.targetCollection.id,
            ShopifyIdType.Collection,
          ),
          100,
        );
        break;
      case "tag:tag":
        groupProducts = await getProductsByTags(
          storefrontClient,
          group.targetTags,
        );
        break;
    }
    for (const product of groupProducts) {
      const existingGroup = groupsByProductId.get(product.id);
      if (existingGroup) {
        // To preserve historical behavior, this group is always the latest exchange group we load,
        // but we now track all groups additionally.
        existingGroup.group = group;
        existingGroup.allGroups.push(group);
      } else {
        groupsByProductId.set(product.id, {
          group,
          product,
          allGroups: [group],
        });
      }
    }
  }
  return groupsByProductId;
}

type RankedProduct = ShopifyStorefrontProduct & { rank: number };
/**
 * Ranks exchange group products with the following priority:
 * - Exchange recommendation rank (Top exchange recommendations with substantial exchange counts)
 * - Part of a returned products collection to collection rank
 * - Part of a returned products collection to collection rank and exchange recommendation rank with lower exchange counts
 * - Order returned from Shopify
 *
 * Higher rank to lower rank (1 is higher rank than 2)
 */
export function rankExchangeGroupProducts(
  groupsByProductId: ExchangeGroupByProductId | undefined,
  exchangeRecommendations: RecommendedExchangeProductData[],
  settings: ReturnAppSettings | undefined,
  originalProduct?: ProductData,
): ShopifyStorefrontProduct[] {
  const rankedProducts: RankedProduct[] = [];

  const excludedProductTags = new Set(
    settings?.exchanges?.excludeProductTags.map((tag: string) =>
      tag.toLowerCase(),
    ),
  );

  // Short circuit, we don't have anything to help us rank the products.
  if (!originalProduct && exchangeRecommendations.length === 0) {
    return Array.from(groupsByProductId?.values() ?? [])
      .map(({ product }) => product)
      .filter((product) => !shouldExcludeProduct(product, excludedProductTags));
  }

  // See if we have same collection to collection exchange group to consider.
  let originalCollectionToSameCollectionId: string | undefined;
  let originalProductShopifyId: string | undefined;
  if (originalProduct) {
    originalProductShopifyId = convertShopifyIdToGid(
      originalProduct.productId,
      ShopifyIdType.Product,
    );
    // If we supplied the original product and it is part of a collection to collection exchange group
    const groups = groupsByProductId?.get(originalProductShopifyId)?.allGroups;
    originalCollectionToSameCollectionId = groups
      ?.filter((group) => group.type === "collection:collection")
      .find((group) => group.sourceCollection.id === group.targetCollection.id)
      ?.sourceCollection.id;
  }

  for (const [
    productId,
    { product, allGroups },
  ] of groupsByProductId?.entries() ?? []) {
    // Should we not even show this product for exchanges? ie. draft product or excluded by tag.
    if (shouldExcludeProduct(product, excludedProductTags)) {
      continue;
    }

    // Initialize to -1 to know we haven't assigned a rank yet.
    let rank = -1;

    // Is the returned product part of a collection to collection exchange group with its own collection?
    if (originalCollectionToSameCollectionId) {
      const isSameProductCollection = !!allGroups
        ?.filter((g) => g.type === "collection:collection")
        .find(
          (g) =>
            g.sourceCollection.id === originalCollectionToSameCollectionId &&
            g.targetCollection.id === originalCollectionToSameCollectionId,
        );

      // We want to assign a rank since we are part of the same collection as the returned product.
      if (isSameProductCollection) {
        // If the product is the same as the original product, it should be ranked as low as possible, they are already trying to exchange this product.
        const isSameProduct = product.id === originalProductShopifyId;
        if (isSameProduct) {
          rank = 1001;
        } else {
          rank = 100;
        }
      }
    }

    const exchangeRecommendationIndex = exchangeRecommendations.findIndex(
      (recommendedProduct) =>
        recommendedProduct.product.productId === productId,
    );

    // Are we are recommended exchange product?
    if (exchangeRecommendationIndex >= 0) {
      if (rank < 0) {
        // We have no ranking from collection to collection, so we should use the recommended exchange rank.
        rank = exchangeRecommendationIndex;
      } else {
        // We are part of a collection to collection exchange group, so we should take that into account.

        // How many data points do we have for exchanges for this product?
        const exchangeRecommendationExchangeCount =
          exchangeRecommendationIndex >= 0
            ? (exchangeRecommendations[exchangeRecommendationIndex]
                ?.exchangeCount ?? 0)
            : 0;

        // More than 1 exchange? Rank this with the recommended exchange rank.
        if (exchangeRecommendationExchangeCount > 1) {
          rank = exchangeRecommendationIndex;
        } else {
          // Low exchange count, put it after the collection to collection rank.
          rank += exchangeRecommendationExchangeCount;
        }
      }
    }

    // We still have no rank, so not exchange recommendation or collection to collection.
    if (rank < 0) {
      rank = 1000;
    }

    rankedProducts.push({ ...product, rank });
  }

  return rankedProducts.sort((a, b) => a.rank - b.rank);
}

function shouldExcludeProduct(
  product: ShopifyStorefrontProduct,
  excludedProductTags: Set<string>,
) {
  return (
    product.publishedAt === null ||
    product.tags.some((tag: string) =>
      excludedProductTags.has(tag.toLowerCase()),
    )
  );
}

export const useExchangeGroups = ({
  settings,
  groups,
  selectProduct,
  submitProduct,
  exchangeRecommendations,
  suggestedExchangeOptions,
  item,
}: {
  settings: ReturnAppSettings | undefined;
  groups: ExchangeGroup[];
  selectProduct: (
    data: ProductVariantData | undefined,
    group: ExchangeGroup | undefined,
    suggestedExchangeResult?: SuggestedExchangeResult,
  ) => void;
  submitProduct: (
    data: ProductVariantData | undefined,
    group: ExchangeGroup | undefined,
    suggestedExchangeResult?: SuggestedExchangeResult,
  ) => void;
  exchangeRecommendations?: RecommendedExchangeProductData[];
  suggestedExchangeOptions?: SuggestedExchangeGroupOptions;
  item: ReturnableItem;
}): UseExchangeGroupsResponse => {
  const returnContext = useContext(ReturnContext);
  const suggestedExchangeOptionsContext = useContext(
    SuggestedExchangeOptionsContext,
  );
  const [selectedProduct, setSelectedProduct] = useState<
    ProductVariantData | undefined
  >();

  const groupsByProductId = useLoad(async () => {
    if (suggestedExchangeOptions?.groupsByProductId) {
      return suggestedExchangeOptions.groupsByProductId;
    }

    const groupsByProductId = await getExchangeGroupsByProductId(
      groups,
      settings,
    );

    if (suggestedExchangeOptions) {
      suggestedExchangeOptionsContext.setExchangeGroupsByProductId(
        groupsByProductId.size > 0 ? groupsByProductId : undefined,
      );
    }

    return groupsByProductId;
  }, []);

  const suggestedExchangeResult = suggestedExchangeOptions?.shouldFireEvent
    ? getSuggestedExchangeResult({
        type: SuggestedExchangeEventType.EXCHANGE_GROUPS,
        fromSuggestedPreview: suggestedExchangeOptions.fromSuggestedPreview,
      })
    : undefined;

  const onSubmitProduct = (selectedProductAndVariant: ProductVariantData) => {
    submitProduct(
      selectedProductAndVariant,
      groupsByProductId.value?.get(selectedProductAndVariant.productId)?.group,
      suggestedExchangeResult,
    );
  };

  const onSelectProduct = (
    selectedProductAndVariant:
      | ProductVariantData
      | { productIdToRemove: string },
  ): void => {
    if ("productIdToRemove" in selectedProductAndVariant) {
      setSelectedProduct(undefined);
      selectProduct(undefined, undefined, undefined);
      return;
    }
    setSelectedProduct(selectedProductAndVariant);
    selectProduct(
      selectedProductAndVariant,
      groupsByProductId.value?.get(selectedProductAndVariant.productId)?.group,
      suggestedExchangeResult,
    );
  };

  const getExchangeGroup = (productId: string) => {
    return groupsByProductId.value?.get(productId)?.group;
  };

  const products = useMemo(() => {
    return rankExchangeGroupProducts(
      groupsByProductId.value,
      exchangeRecommendations ?? [],
      settings,
      suggestedExchangeOptions?.originalProduct,
    );
  }, [
    groupsByProductId.value,
    exchangeRecommendations,
    settings,
    suggestedExchangeOptions?.originalProduct,
  ]);

  const totalReturnableValue = useMemo(() => {
    return getReturnableItemTotalReturnValue(item, 1).toNumber();
  }, [item]);

  return {
    products: products.map((product) =>
      storefrontProductToProductData(
        product,
        settings,
        returnContext.draftReturn?.returnType,
      ),
    ),
    selectedProduct,
    onSelectProduct,
    onSubmitProduct,
    title: suggestedExchangeOptions
      ? "Exchange for a similar item"
      : "Choose the new item you would like",
    subtitle: suggestedExchangeOptions
      ? "Select a product you would like to exchange for"
      : "Select an item to proceed.",
    loading: groupsByProductId.pending,
    totalReturnableValue,
    getExchangeGroup,
  };
};
